import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Upload, X, ImagePlus, Sparkles, Mail, Package, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, errText, CONDITIONS, formatPrice } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";

const CLOTH_SIZES = ["XS", "S", "M", "L", "XL", "XXL", "32", "34", "36", "38", "40", "42", "44", "46", "48", "50", "52", "54", "56"];
const SHOE_SIZES = ["35", "36", "37", "38", "39", "40", "41", "42", "43", "44", "45", "46", "47", "48", "49"];
const KIDS_SIZES = ["50", "56", "62", "68", "74", "80", "86", "92", "98", "104", "110", "116", "122", "128", "134", "140", "146", "152", "158", "164", "170", "176"];

function ShippingSection({ form, setForm }) {
  const [sizes, setSizes] = useState([]);
  useEffect(() => { api.get("/shipping/packages").then(({ data }) => setSizes(data.sizes)).catch(() => {}); }, []);
  const sel = sizes.find((s) => s.key === form.package_size);
  const toggleCarrier = (k) => setForm((f) => ({ ...f, carriers: f.carriers.includes(k) ? f.carriers.filter((c) => c !== k) : [...f.carriers, k] }));
  return (
    <div data-testid="sell-shipping-section">
      <h2 className="text-lg font-bold">Wie möchtest du versenden?</h2>
      <p className="text-sm text-muted-foreground mt-1 mb-4">Wähle die passende Paketgröße für deinen Artikel.</p>
      <div className="space-y-3">
        {sizes.map((s) => {
          const active = form.package_size === s.key;
          const Icon = s.key === "GROSSBRIEF" || s.key === "WARENSENDUNG" ? Mail : Package;
          return (
            <button type="button" key={s.key} data-testid={`package-size-${s.key}`}
              onClick={() => setForm((f) => ({ ...f, package_size: s.key, carriers: [] }))}
              className={`w-full flex items-start gap-3.5 text-left border rounded-2xl p-4 transition-colors ${active ? "border-accent bg-accent/5" : "border-border bg-card hover:border-foreground/30"}`}>
              <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${active ? "bg-accent text-white" : "bg-secondary text-muted-foreground"}`}>
                <Icon className="w-5 h-5" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-bold">{s.label}</span>
                <span className="block text-xs text-muted-foreground mt-0.5">{s.desc}</span>
                <span className="block text-[11px] text-muted-foreground/80 mt-1.5">{s.dims} &nbsp;·&nbsp; {s.max_weight}</span>
              </span>
              <span className="text-right shrink-0">
                <span className="block text-sm font-extrabold text-accent">ab {formatPrice(s.from_price)}</span>
                {active && <span className="inline-block mt-1.5 text-[10px] font-bold bg-accent text-white rounded-full px-2.5 py-0.5">Ausgewählt</span>}
              </span>
            </button>
          );
        })}
      </div>
      {sel && (
        <div className="border border-border rounded-2xl p-4 mt-4" data-testid="sell-carrier-options">
          <p className="text-sm font-bold mb-3">Versandoptionen</p>
          <div className="space-y-2">
            {sel.carriers.map((c) => (
              <label key={c.key} data-testid={`carrier-option-${c.key}`}
                className={`flex items-center justify-between border rounded-xl px-4 py-3 cursor-pointer transition-colors ${form.carriers.includes(c.key) ? "border-accent bg-accent/5" : "border-border hover:border-foreground/30"}`}>
                <span className="flex items-center gap-3 text-sm">
                  <input type="checkbox" className="accent-[#FF6B35]" checked={form.carriers.includes(c.key)} onChange={() => toggleCarrier(c.key)} />
                  <span>
                    <span className="font-semibold block">{c.label}</span>
                    <span className="text-xs text-muted-foreground">{c.time} · {c.tracking ? "Mit Sendungsverfolgung" : "Ohne Sendungsverfolgung"}</span>
                  </span>
                </span>
                <span className="text-sm font-bold">{formatPrice(c.price)}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      <label data-testid="sell-pickup-toggle"
        className={`mt-3 flex items-center gap-3 border rounded-2xl px-4 py-3.5 cursor-pointer transition-colors ${form.pickup ? "border-accent bg-accent/5" : "border-border hover:border-foreground/30"}`}>
        <input type="checkbox" className="accent-[#FF6B35]" checked={form.pickup} onChange={(e) => setForm((f) => ({ ...f, pickup: e.target.checked }))} />
        <MapPin className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm font-semibold">Abholung verfügbar</span>
      </label>
      <div className={`mt-3 flex items-center justify-between border rounded-2xl px-4 py-3.5 ${form.free_shipping ? "border-accent bg-accent/5" : "border-border"}`}>
        <div>
          <p className="text-sm font-semibold">Kostenloser Versand</p>
          <p className="text-xs text-muted-foreground">Du übernimmst die Versandkosten</p>
        </div>
        <Switch data-testid="sell-free-shipping-switch" checked={form.free_shipping} onCheckedChange={(v) => setForm((f) => ({ ...f, free_shipping: v }))} />
      </div>
    </div>
  );
}

export default function Sell() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("edit");
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ai, setAi] = useState(null);
  const [form, setForm] = useState({
    title: "", description: "", category_id: "", subcategory_id: null, condition: "SEHR_GUT",
    size: "", color: "", price: "", location: "",
    package_size: "MITTEL", carriers: [], pickup: false, free_shipping: false,
    weight: "", weight_unit: "g",
    negotiable: true, min_price: "", brand_id: null, authenticity_confirmed: false,
  });

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
    api.get("/brands").then(({ data }) => setBrands(data)).catch(() => {});
    if (editId) {
      api.get(`/items/${editId}`).then(({ data }) => {
        setForm({ title: data.title, description: data.description, category_id: data.category_id,
          subcategory_id: data.subcategory_id || null,
          condition: data.condition, size: data.size || "", color: data.color || "",
          price: String(data.price), location: data.location || "",
          package_size: data.package_size || "MITTEL",
          carriers: (data.shipping_methods || []).filter((m) => !["ABHOLUNG", "STANDARD"].includes(m)),
          pickup: (data.shipping_methods || []).includes("ABHOLUNG"),
          free_shipping: !!data.free_shipping,
          weight: data.weight ? String(data.weight) : "", weight_unit: data.weight_unit || "g",
          negotiable: data.negotiable !== false, min_price: data.min_price ? String(data.min_price) : "",
          brand_id: data.brand_id || null, authenticity_confirmed: !!data.authenticity_confirmed });
        setImages(data.image_ids.map((id) => ({ image_id: id })));
      }).catch(() => {});
    }
  }, [editId]);

  const catById = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c])), [cats]);
  const sizeKind = useMemo(() => {
    const c = catById[form.category_id];
    if (!c) return "free";
    const top = c.parent_id ? catById[c.parent_id] : c;
    const slugs = `${c.slug || ""} ${top?.slug || ""}`;
    if (/schuhe|sneaker|stiefel|pumps|sandalen|ballerina/.test(slugs)) return "shoes";
    if (top?.slug === "kinder") return "kids";
    if (["damen", "herren"].includes(top?.slug)) return "clothing";
    return "free";
  }, [form.category_id, catById]);
  const sizeList = sizeKind === "shoes" ? SHOE_SIZES : sizeKind === "kids" ? KIDS_SIZES : CLOTH_SIZES;

  const analyze = async (imageId) => {
    setAi("busy");
    try {
      const { data } = await api.post("/items/analyze-image", { image_id: imageId });
      if (!data.category_id && !data.brand_id) { setAi(null); return; }
      setAi(data);
      setForm((f) => ({
        ...f,
        category_id: f.category_id || data.category_id || "",
        subcategory_id: f.subcategory_id || data.subcategory_id || null,
        brand_id: f.brand_id || data.brand_id || null,
      }));
    } catch { setAi(null); }
  };

  const uploadFiles = async (files) => {
    setUploading(true);
    const hadImages = images.length > 0;
    let firstId = null;
    for (const file of [...files].slice(0, 8 - images.length)) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (!firstId) firstId = data.image_id;
        setImages((prev) => [...prev, data]);
      } catch (e) { toast.error(errText(e)); }
    }
    setUploading(false);
    if (!hadImages && firstId && !editId) analyze(firstId);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (images.length === 0) return toast.error("Bitte lade mindestens ein Foto hoch");
    const methods = [...form.carriers, ...(form.pickup ? ["ABHOLUNG"] : [])];
    if (methods.length === 0) return toast.error("Bitte wähle mindestens eine Versandoption oder biete Abholung an");
    setBusy(true);
    const payload = {
      ...form, price: parseFloat(form.price), shipping_price: 0,
      image_ids: images.map((i) => i.image_id), shipping_methods: methods,
      package_size: form.package_size, free_shipping: form.free_shipping, status: "ACTIVE",
      brand: null, size: form.size || null, color: form.color || null, location: form.location || null,
      weight: form.weight ? parseFloat(form.weight) : null, weight_unit: form.weight_unit || "g",
      negotiable: form.negotiable, min_price: form.negotiable && form.min_price ? parseFloat(form.min_price) : null,
    };
    delete payload.carriers;
    delete payload.pickup;
    try {
      const { data } = editId ? await api.put(`/items/${editId}`, payload) : await api.post("/items", payload);
      if (data.status === "REVIEW_REQUIRED") {
        toast("Artikel in Prüfung", { description: "Dein Artikel wird vor der Veröffentlichung manuell geprüft." });
      } else {
        toast.success(editId ? "Artikel aktualisiert" : "Artikel veröffentlicht", { description: editId ? undefined : "Dein Artikel ist jetzt auf KLEDIO sichtbar." });
      }
      nav(`/artikel/${data.id}`);
    } catch (err) { toast.error(errText(err)); } finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const input = "w-full rounded-xl border border-border bg-card px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <h1 className="text-2xl sm:text-4xl font-bold">{editId ? "Artikel bearbeiten" : "Artikel verkaufen"}</h1>
      <p className="text-muted-foreground text-sm mt-2">Fotos hochladen, beschreiben, Preis festlegen — fertig.</p>

      <form onSubmit={submit} className="mt-6 sm:mt-8 space-y-6">
        <div>
          <label className="text-sm font-semibold mb-2 block">Fotos (max. 8)</label>
          <div className="grid grid-cols-4 gap-3">
            {images.map((img, i) => (
              <div key={img.image_id} className="relative aspect-square rounded-xl overflow-hidden border border-border">
                <img src={imgUrl(img.image_id, "thumb")} alt="" className="w-full h-full object-cover" />
                <button type="button" data-testid={`remove-image-${i}`} onClick={() => setImages(images.filter((x) => x.image_id !== img.image_id))}
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1.5"><X className="w-3 h-3" /></button>
              </div>
            ))}
            {images.length < 8 && (
              <label data-testid="image-upload-label" className="aspect-square rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:bg-secondary/50 transition-colors text-muted-foreground">
                {uploading ? <Upload className="w-5 h-5 animate-pulse" /> : <ImagePlus className="w-5 h-5" />}
                <span className="text-[10px] mt-1">{uploading ? "Lädt…" : "Foto"}</span>
                <input data-testid="image-upload-input" type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} />
              </label>
            )}
          </div>
          {ai === "busy" && (
            <div data-testid="ai-analyzing" className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
              <Sparkles className="w-4 h-4 text-accent animate-pulse" /> KLEDIO-KI analysiert dein Foto…
            </div>
          )}
          {ai && ai !== "busy" && (
            <div data-testid="ai-suggestion-banner" className="mt-3 border border-accent/40 bg-accent/5 rounded-2xl p-3.5 flex items-start gap-2.5">
              <Sparkles className="w-4 h-4 text-accent mt-0.5 shrink-0" />
              <div className="text-sm flex-1">
                <span className="font-bold">KI-Vorschlag übernommen:</span>{" "}
                {[ai.category_name, ai.subcategory_name].filter(Boolean).join(" › ")}
                {ai.brand_name ? ` · Marke: ${ai.brand_name}` : ""}
                <span className="block text-xs text-muted-foreground mt-0.5">Du kannst Kategorie und Marke jederzeit manuell ändern.</span>
              </div>
              <button type="button" data-testid="ai-dismiss-btn" onClick={() => setAi(null)} className="text-muted-foreground p-1"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
        </div>

        <input data-testid="sell-title-input" className={input} placeholder="Titel (z. B. Levi's 501 Jeans, W32)" value={form.title} onChange={set("title")} required minLength={3} />
        <textarea data-testid="sell-description-input" className={`${input} min-h-32`} placeholder="Beschreibung: Zustand, Details, Maße…" value={form.description} onChange={set("description")} required minLength={5} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-semibold mb-2 block">Kategorie</label>
            <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v, subcategory_id: null })} required>
              <SelectTrigger data-testid="sell-category-select"><SelectValue placeholder="Wählen…" /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.parent_id ? `${catById[c.parent_id]?.name || ""} › ${c.name}` : c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-semibold mb-2 block">Zustand</label>
            <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
              <SelectTrigger data-testid="sell-condition-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(CONDITIONS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-semibold mb-2 block">Marke</label>
            <Select value={form.brand_id || "none"} onValueChange={(v) => setForm({ ...form, brand_id: v === "none" ? null : v, authenticity_confirmed: false })}>
              <SelectTrigger data-testid="sell-brand-select"><SelectValue placeholder="Keine Marke" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Keine Marke</SelectItem>
                {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}{b.high_risk ? " · Luxusmarke" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-semibold mb-2 block">Größe</label>
            {sizeKind === "free" ? (
              <input data-testid="sell-size-input" className={input} placeholder="Größe (optional)" value={form.size} onChange={set("size")} />
            ) : (
              <Select value={form.size || "none"} onValueChange={(v) => setForm({ ...form, size: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="sell-size-select"><SelectValue placeholder="Größe wählen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keine Angabe</SelectItem>
                  {sizeList.map((s) => <SelectItem key={s} value={s}>{sizeKind === "shoes" ? `EU ${s}` : s}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <div>
            <label className="text-sm font-semibold mb-2 block">Farbe</label>
            <input data-testid="sell-color-input" className={input} placeholder="Farbe" value={form.color} onChange={set("color")} />
          </div>
        </div>

        {form.brand_id && (
          <div className="border border-accent/40 bg-accent/5 rounded-2xl p-4 space-y-2" data-testid="authenticity-box">
            <p className="text-xs font-bold text-foreground">Fälschungen und Markenimitate sind auf KLEDIO nicht erlaubt.</p>
            <label className="flex items-start gap-2.5 text-sm cursor-pointer">
              <input type="checkbox" data-testid="authenticity-checkbox" checked={form.authenticity_confirmed}
                onChange={(e) => setForm({ ...form, authenticity_confirmed: e.target.checked })} className="mt-0.5 accent-[#FF6B35]" />
              <span>Ich bestätige, dass dieser Artikel authentisch ist und keine Fälschung oder Replica darstellt.</span>
            </label>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <div>
            <label className="text-sm font-semibold mb-2 block">Preis (€)</label>
            <input data-testid="sell-price-input" className={input} type="number" step="0.01" min="0.5" placeholder="25.00" value={form.price} onChange={set("price")} required />
          </div>
          <div>
            <label className="text-sm font-semibold mb-2 block">Standort</label>
            <input data-testid="sell-location-input" className={input} placeholder="z. B. Berlin" value={form.location} onChange={set("location")} />
          </div>
        </div>

        <ShippingSection form={form} setForm={setForm} />

        <div>
          <label className="text-sm font-semibold mb-2 block">Gewicht (optional)</label>
          <div className="flex gap-2 max-w-xs">
            <input data-testid="sell-weight-input" className={input} type="number" step="any" min="0" placeholder={form.weight_unit === "kg" ? "z. B. 1.2" : "z. B. 450"} value={form.weight} onChange={set("weight")} />
            <Select value={form.weight_unit} onValueChange={(v) => setForm({ ...form, weight_unit: v })}>
              <SelectTrigger data-testid="sell-weight-unit-select" className="w-24 shrink-0"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="g">g</SelectItem>
                <SelectItem value="kg">kg</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="border border-border rounded-2xl p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">Verhandlungen erlauben</p>
              <p className="text-xs text-muted-foreground">Käufer können dir Preisangebote senden</p>
            </div>
            <Switch data-testid="sell-negotiable-switch" checked={form.negotiable} onCheckedChange={(v) => setForm({ ...form, negotiable: v })} />
          </div>
          {form.negotiable && (
            <div>
              <label className="text-sm font-semibold mb-2 block">Mindestpreis (€, optional — für Käufer unsichtbar)</label>
              <input data-testid="sell-minprice-input" className={input} type="number" step="0.01" min="0.5" placeholder="z. B. 80.00" value={form.min_price} onChange={set("min_price")} />
            </div>
          )}
        </div>

        <button data-testid="sell-submit-btn" disabled={busy || uploading}
          className="w-full bg-accent text-accent-foreground font-bold py-4 rounded-full hover:opacity-90 active:scale-[0.98] transition-transform disabled:opacity-50">
          {busy ? "Wird veröffentlicht…" : editId ? "Änderungen speichern" : "Artikel veröffentlichen"}
        </button>
      </form>
    </div>
  );
}
